import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiPhone, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { isBusinessApp } from '../../utils/platform';

/**
 * One-time prompt asking users to add a phone number. Triggered when the
 * authenticated user has no phone on file — primarily social-signin users
 * (Google/Apple don't share phone) and accounts from before phone was asked.
 *
 * Design:
 *   - Mounted once at the App root, inside AuthProvider.
 *   - Skips auth routes (/login, /register, etc.) so unauthenticated
 *     users don't see the prompt after logout.
 *   - Skips ADMIN users — admin tools shouldn't be nagged by a client UX.
 *   - The modal contains the phone form itself (no redirect to /profile
 *     needed). On successful save, checkAuth() refreshes the user so
 *     the prompt closes automatically.
 *   - DISMISSIBLE ("Mai târziu") and remembered per user on this device.
 *     Apple 5.1.1(v) forbids REQUIRING data that isn't strictly necessary
 *     for the core function — the phone is useful (salon contact), not
 *     necessary, so it must stay optional. The app rejection quoted this
 *     exact gate; never make it blocking again.
 */
const SKIP_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/landing',
  // The two-step salon signup: a fresh social CLIENT lands on /pro/register
  // step 2 with phone=null — popping this modal over "name your salon" (and
  // burning the one-time prompt on a reflexive "Mai târziu") would sabotage
  // the flagship flow. NB: startsWith('/register') does NOT match '/pro/…'.
  '/pro/register',
  '/pro/login',
];

const snoozeKey = (userId) => `petzy_phone_prompt_snoozed:${userId}`;

export default function MissingPhoneGate() {
  const { user, checkAuth } = useContext(AuthContext) || {};
  const { pathname } = useLocation();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);

  // Business app: owners register with a mandatory phone and specialists get
  // theirs from the roster — this client-flow nudge has no audience there,
  // and it would fire over the in-app salon-registration step 2.
  if (isBusinessApp) return null;
  // Don't render on auth routes or when no user is loaded yet.
  if (!user) return null;
  if (SKIP_PATHS.some((p) => pathname.startsWith(p))) return null;
  // Admins shouldn't be locked out of ops tools by a client-flow gate.
  if (user.role === 'ADMIN') return null;
  // User already has a phone — nothing to do.
  const existing = typeof user.phone === 'string' ? user.phone.trim() : '';
  if (existing.length >= 7) return null;
  // Already declined (this session or on a previous login) — don't nag.
  if (dismissed) return null;
  try { if (localStorage.getItem(snoozeKey(user.id)) === '1') return null; } catch { /* noop */ }

  const dismiss = () => {
    try { localStorage.setItem(snoozeKey(user.id), '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (trimmed.length < 7) {
      setError('Numarul de telefon trebuie sa aiba cel putin 7 caractere.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.put('/auth/profile', { phone: trimmed });
      await checkAuth?.();
      toast.success('Numar de telefon salvat. Iti multumim!');
      // checkAuth updates user.phone, which causes this component to
      // unmount on the next render — no explicit close needed.
    } catch (err) {
      const msg = err?.response?.data?.message || 'Nu am putut salva numarul. Incearca din nou.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
            <FiAlertCircle className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Adaugă un număr de telefon</h2>
            <p className="text-sm text-gray-600 mt-1">
              Opțional, dar recomandat: salonul te poate contacta rapid dacă apare ceva legat de programarea ta.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Numar de telefon
            </label>
            <div className="relative">
              <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07xx xxx xxx"
                minLength={7}
                required
                autoFocus
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors shadow-sm disabled:opacity-50"
          >
            {saving ? 'Se salveaza...' : 'Salveaza si continua'}
          </button>

          <button
            type="button"
            onClick={dismiss}
            className="w-full py-3 text-gray-500 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            Mai târziu
          </button>

          <p className="text-xs text-gray-400 text-center">
            Folosim numarul doar pentru a te contacta cu privire la programarile tale.
            Îl poți adăuga oricând din profil.
          </p>
        </form>
      </div>
    </div>
  );
}
